import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { getProductsByCategory } from '../../services/productService';
import { getMySubmission, getMySubmissions } from '../../services/intakeService';
import { hasSubmissionForProduct } from '../../utils/intakeRouting';
import { DOCTOR_NOW_DOSAGE_NOTICE, canCustomerSelectDosage } from '../../utils/doctorNowDosageNotice';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import OfferLoadingScreen from '../../components/common/OfferLoadingScreen';
import ConsentDisclaimer from '../../components/common/ConsentDisclaimer';
import styles from './OfferPage.module.css';
import SupplyMonthSelector from '../../components/product/SupplyMonthSelector';
import { applyPromoDiscount, getAdjustedSupplyPricing } from '../../utils/promoUtils';
import { resolveLoadErrorMessage } from '../../utils/errorMessages';
// Helper: filter to only active dosages
const getActiveDosages = (dosages) => {
  if (!dosages || !Array.isArray(dosages)) return [];
  return dosages.filter(d => d.isActive !== false);
};

// Helper: format dosage option with price
const formatDosageOption = (dosage) => {
  if (!dosage) return "";
  const price = dosage.price ? ` - $${Math.ceil(dosage.price / 100)}` : "";
  return `${dosage.amount}${dosage.description ? ` - ${dosage.description}` : ""}${price}`;
};

// Helper: derive the retail-vs-Oval comparison for one dosage.
//
// ⚠️ It takes the dosage's own LIST price as well as the price actually on screen, and both
// matter. `displayedPrice` is term- and promo-discounted, so it is always at or below `listPrice`;
// gating on it alone would fire for a dosage whose retail EQUALS its list price — a product with
// no retail discount at all — and manufacture a saving the data does not support. So the GUARD
// asks the data question ("does this dosage carry a retail premium over its own list price?")
// while the PERCENTAGE answers the display question ("how far below retail is the price shown?").
// Returns null whenever there is nothing truthful to claim, so no caller can render a "$0" strike
// or a "0% off" badge.
const deriveRetailSavings = (retailPrice, listPrice, displayedPrice) => {
  const shownPrice = displayedPrice || listPrice;
  if (!retailPrice || !listPrice || !shownPrice) return null;
  if (retailPrice <= listPrice || retailPrice <= shownPrice) return null;
  return {
    retailDisplay: `$${Math.ceil(retailPrice / 100)}`,
    savingsPercent: Math.round(((retailPrice - shownPrice) / retailPrice) * 100)
  };
};

// Helper: the dosage a "from $X" quote is built from — the CHEAPEST active dosage — so the strike
// and the quoted price describe the same dosage rather than pairing a headline price with an
// unrelated dosage's retail.
const getStartingDosage = (dosages) => {
  const priced = getActiveDosages(dosages).filter((d) => d.price > 0);
  if (priced.length === 0) return null;
  return priced.reduce((min, d) => (d.price < min.price ? d : min), priced[0]);
};

// Retail strikethrough + savings badge. Deliberately mirrors the checkout order summary
// (CheckoutPage.jsx `.retailPriceStrike`) so the offer page and the order summary make the same
// claim in the same visual language.
const RetailSavings = ({ retailPrice, listPrice, displayedPrice }) => {
  const savings = deriveRetailSavings(retailPrice, listPrice, displayedPrice);
  if (!savings) return null;
  return (
    <span className={styles.retailCompare}>
      <span className={styles.retailPriceStrike}>{savings.retailDisplay}</span>
      <span className={styles.savingsBadge}>Save {savings.savingsPercent}%</span>
    </span>
  );
};


const OfferPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromIntake = searchParams.get('from') === 'intake';
  const originalProductSlug = searchParams.get('originalProduct');
  const { user } = useAuth();
  const { toast } = useToast();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDosage, setSelectedDosage] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [supplyMonths, setSupplyMonths] = useState(3);
  const [otherSupplyMonths, setOtherSupplyMonths] = useState({});
  const [otherDosages, setOtherDosages] = useState({}); // Selected dosage per alternative product

  // Handler to update supply months for alternative products
  const handleOtherSupplyMonthsChange = useCallback((productId, months) => {
    setOtherSupplyMonths(prev => ({ ...prev, [productId]: months }));
  }, []);

  // Handler to update selected dosage for alternative products
  const handleOtherDosageChange = useCallback((productId, dosage) => {
    setOtherDosages(prev => ({ ...prev, [productId]: dosage }));
  }, []);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [checkingIntake, setCheckingIntake] = useState(false);

  // Check if loading screen has already been shown this session for this offer
  const sessionKey = `superOffers_loadingShown_${slug}`;
  const hasShownLoading = sessionStorage.getItem(sessionKey) === 'true';
  const [animationDone, setAnimationDone] = useState(hasShownLoading);

  const handleAnimationComplete = useCallback(() => {
    setAnimationDone(true);
    // Mark that loading has been shown for this offer
    sessionStorage.setItem(sessionKey, 'true');
  }, [sessionKey]);

  const showLoading = !animationDone || loading;

  useEffect(() => {
    const loadOffer = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/offers/${slug}`);
        setOffer(response.offer);
      } catch (err) {
        console.error('Failed to load offer:', err);
        setError(resolveLoadErrorMessage(err, 'Failed to load offer'));
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadOffer();
    }
  }, [slug]);

  // Fetch all products from the offer's category when offer loads.
  // Named + memoised so the error state can offer a retry that re-runs exactly this.
  const fetchCategoryProducts = useCallback(async () => {
    if (!offer?.category?.slug) return;

    try {
      setLoadingProducts(true);
      setProductsError(null);
      const response = await getProductsByCategory(offer.category.slug);
      setCategoryProducts(response.products || []);
    } catch (err) {
      console.error('Failed to load category products:', err);
      // Do NOT let a FAILURE fall through to the empty list: `categoryProducts.length === 0`
      // renders "No products available", which tells a prospective customer the catalogue is
      // empty. Record the failure so the scroller can say so and offer a retry instead.
      setCategoryProducts([]);
      setProductsError(resolveLoadErrorMessage(err, 'We could not load the treatment options.'));
    } finally {
      setLoadingProducts(false);
    }
  }, [offer?.category?.slug]);

  useEffect(() => {
    if (offer) {
      fetchCategoryProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.category?.slug]);

  // Toggle "View All Options" scroller
  const handleToggleAllProducts = useCallback(() => {
    setShowAllProducts((prev) => !prev);
  }, []);

  // Get featured product (first one or marked as featured)
  const featuredProduct = offer?.products?.find(p => p.isFeatured) || offer?.products?.[0];

  // Set default dosage when featured product changes (only active dosages)
  useEffect(() => {
    const activeDosages = getActiveDosages(featuredProduct?.product?.dosages);
    if (activeDosages.length > 0) {
      const defaultDosage = activeDosages.find(d => d.isDefault) || activeDosages[0];
      setSelectedDosage(defaultDosage);
    } else {
      setSelectedDosage(null);
    }
  }, [featuredProduct?.product?.id]);

  const handleDosageChange = (e) => {
    const dosageId = e.target.value;
    const activeDosages = getActiveDosages(featuredProduct?.product?.dosages);
    const dosage = activeDosages.find(d => d.id === dosageId);
    setSelectedDosage(dosage || null);
  };

  // Navigate to checkout with product and dosage
  const navigateToCheckout = useCallback((product, dosageId, months) => {
    const params = new URLSearchParams({ product: product.product.slug });
    if (dosageId) {
      params.set('dosage', dosageId);
    }
    if (months) {
      params.set('supplyMonths', months.toString());
    }
    if (slug) { params.set('offer', slug); }
    navigate(`/calendar?${params.toString()}`);
  }, [navigate, slug]);

  // Handle product selection with intake check (for legacy products)
  // A single intake lookup reduced to 'has' | 'none' | 'unknown'.
  // 'unknown' means the call genuinely THREW and is deliberately NOT collapsed into
  // 'none' -- "we could not ask" is not "you never submitted".
  const checkIntakeLookup = async (label, runLookup) => {
    try {
      return (await runLookup()) ? 'has' : 'none';
    } catch (err) {
      // Card 53622 c5. `web/services/api.js` throws on EVERY non-2xx, so the ordinary
      // "no submission on file" answer arrives here as a 404 -- that is the server
      // ANSWERING, not a failure to ask. Folding it into 'unknown' blocked every
      // customer who had not yet completed intake from ever reaching the intake form,
      // which is the state of every new customer on every product. The transport/5xx
      // failure this card exists for carries no `status` and still resolves to
      // 'unknown'. NOTE: `code` cannot be used to tell the two 404s apart -- both
      // "Submission not found" and "Route not found" carry code 'NOT_FOUND' -- and a
      // route-level 404 is still settled truthfully by the independent
      // `getMySubmissions` fallback below.
      if (err?.status === 404) return 'none';
      console.error(`${label} failed:`, err);
      return 'unknown';
    }
  };

  // Card 53622, on the defect card 6177 disclosed and deliberately left out of its own
  // scope: both consumers below wrapped each lookup in a bare `catch (e) {}`, so a 500 or
  // a dropped connection was indistinguishable from a truthful "no submission" and a
  // customer who HAD completed intake was routed back to re-fill it.
  // The unfiltered `getMySubmissions` fallback is KEPT on purpose -- #6177 records it as
  // real resilience for the per-product endpoint failing, and dropping it is a separate
  // decision. Only its SILENCE is fixed here.
  const deriveIntakeStatus = useCallback(async (productId) => {
    const perProduct = await checkIntakeLookup(
      'Per-product intake lookup',
      () => getMySubmission(productId),
    );
    if (perProduct === 'has') return 'has';

    const fallback = await checkIntakeLookup(
      'Intake submissions fallback',
      async () => hasSubmissionForProduct(await getMySubmissions(), productId),
    );
    if (fallback === 'has') return 'has';

    // Only claim "no submission" when BOTH lookups actually answered.
    return perProduct === 'unknown' || fallback === 'unknown' ? 'unknown' : 'none';
  }, []);

  const handleProductSelect = useCallback(async (product, productId) => {
    if (!product?.product?.slug || !product?.product?.id) return;

    // Show visual selection feedback
    setSelectedProductId(productId);

    // Determine dosage ID
    let dosageId = null;
    if (product === featuredProduct && selectedDosage) {
      dosageId = selectedDosage.id;
    } else {
      // Use selected dosage from otherDosages state, or fall back to default active dosage
      const selectedOtherDosage = otherDosages[productId];
      if (selectedOtherDosage) {
        dosageId = selectedOtherDosage.id;
      } else {
        const activeDosages = getActiveDosages(product.product.dosages);
        if (activeDosages.length > 0) {
          const defaultDosage = activeDosages.find(d => d.isDefault) || activeDosages[0];
          dosageId = defaultDosage.id;
        }
      }
    }

    // Determine supply months
    const months = product === featuredProduct
      ? supplyMonths
      : (otherSupplyMonths[productId] || 3);

    // If user is not logged in, send to intake page (which will require login)
    if (!user) {
      setTimeout(() => {
        (() => { sessionStorage.setItem('selectedSupplyMonths', months?.toString() || supplyMonths.toString()); if (dosageId) sessionStorage.setItem('selectedDosageId', dosageId); navigate(`/intake/${product.product.slug}?offer=${slug}${dosageId ? '&dosage=' + dosageId : ''}`); })();
      }, 150);
      return;
    }

    // If user just came from intake form, skip submission check — go straight to checkout
    if (fromIntake) {
      setTimeout(() => {
        navigateToCheckout(product, dosageId, months);
      }, 150);
      return;
    }

    // Check if user has completed intake for this product
    setCheckingIntake(true);
    try {
      const intakeStatus = await deriveIntakeStatus(product.product.id);
      setCheckingIntake(false);

      // "We could not ask" is not "you never submitted", so do not guess in either
      // direction: guessing 'none' sends a customer who already completed intake back to
      // re-fill it, and guessing 'has' would route an unconfirmed one PAST it. Say so and
      // let them retry -- the CTA is re-armed by clearing the selection.
      if (intakeStatus === 'unknown') {
        setSelectedProductId(null);
        toast.error('We could not confirm your intake form. Please try again.');
        return;
      }

      if (intakeStatus === 'has') {
        setTimeout(() => {
          navigateToCheckout(product, dosageId, months);
        }, 150);
      } else {
        setTimeout(() => {
          (() => { sessionStorage.setItem('selectedSupplyMonths', months?.toString() || supplyMonths.toString()); if (dosageId) sessionStorage.setItem('selectedDosageId', dosageId); navigate(`/intake/${product.product.slug}?offer=${slug}${dosageId ? '&dosage=' + dosageId : ''}`); })();
        }, 150);
      }
    } catch (err) {
      setCheckingIntake(false);
      console.error('Failed to check intake submission:', err);
      setTimeout(() => {
        navigateToCheckout(product, dosageId, months);
      }, 150);
    }
  }, [featuredProduct, selectedDosage, supplyMonths, otherSupplyMonths, otherDosages, navigate, navigateToCheckout, user, slug, deriveIntakeStatus, toast]);

  // Handle linked product offer selection with intake check
  const handleLinkedOfferSelect = useCallback(async (productOffer, cardId) => {
    const product = productOffer?.product;
    if (!product?.slug || !product?.id) return;

    // Show visual selection feedback
    setSelectedProductId(cardId);

    // If user is not logged in, send to intake page
    if (!user) {
      setTimeout(() => {
        navigate(`/intake/${product.slug}?offer=${slug}`);
      }, 150);
      return;
    }

    // If user just came from intake, skip check
    if (fromIntake) {
      setTimeout(() => {
        navigate(`/calendar?product=${product.slug}`);
      }, 150);
      return;
    }

    setCheckingIntake(true);
    try {
      const intakeStatus = await deriveIntakeStatus(product.id);
      setCheckingIntake(false);

      // "We could not ask" is not "you never submitted", so do not guess in either
      // direction: guessing 'none' sends a customer who already completed intake back to
      // re-fill it, and guessing 'has' would route an unconfirmed one PAST it. Say so and
      // let them retry -- the CTA is re-armed by clearing the selection.
      if (intakeStatus === 'unknown') {
        setSelectedProductId(null);
        toast.error('We could not confirm your intake form. Please try again.');
        return;
      }

      if (intakeStatus === 'has') {
        setTimeout(() => {
          navigate(`/calendar?product=${product.slug}`);
        }, 150);
      } else {
        setTimeout(() => {
          navigate(`/intake/${product.slug}?offer=${slug}`);
        }, 150);
      }
    } catch (err) {
      setCheckingIntake(false);
      setTimeout(() => {
        navigate(`/calendar?product=${product.slug}`);
      }, 150);
    }
  }, [navigate, user, slug, fromIntake, deriveIntakeStatus, toast]);

  // Handle skip button — continue with original product (for product-specific triggers)
  const handleSkip = useCallback(() => {
    if (!originalProductSlug) return;
    const supplyM = sessionStorage.getItem('selectedSupplyMonths') || '';
    navigate(`/calendar?product=${originalProductSlug}${supplyM ? '&supplyMonths=' + supplyM : ''}`);
  }, [originalProductSlug, navigate]);

  // Parse headline with highlights (text between ** markers becomes highlighted)
  // eslint-disable-next-line no-unused-vars
  const renderHeadline = (headline) => {
    if (!headline) return null;

    const parts = headline.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const text = part.slice(2, -2);
        return (
          <span key={index} className={styles.highlight}>
            {text}
          </span>
        );
      }
      return part;
    });
  };

  if (showLoading) {
    return <OfferLoadingScreen onComplete={handleAnimationComplete} />;
  }

  if (error || !offer) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>
          <h2>Offer Not Found</h2>
          <p>{error || 'This offer page does not exist or has been removed.'}</p>
          <button onClick={() => navigate('/catalog')}>View Catalog</button>
        </div>
      </div>
    );
  }

  if (!offer.isActive) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>
          <h2>Offer Unavailable</h2>
          <p>This offer is currently not active.</p>
          <button onClick={() => navigate('/catalog')}>View Catalog</button>
        </div>
      </div>
    );
  }

  const alternativeProducts = offer.products?.filter(p => p !== featuredProduct) || [];

  // Linked product offers from super offer
  const linkedOffers = offer?.linkedProductOffers || [];
  const hasLinkedOffers = linkedOffers.length > 0;

  // Parse settings for additional data
  const settings = offer.settings || {};
  // eslint-disable-next-line no-unused-vars
  const headline = settings.headline || offer.shortDescription;
  const features = settings.features || [];
  const stats = settings.stats || {};
  const footnote = settings.footnote || '';

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Linked Product Offers - renders if super offer has selectedProductOfferIds */}
        {hasLinkedOffers ? (
          <>
            <h1 className={styles.linkedOffersTitle}>{offer.title || 'Special Offers'}</h1>
            {offer.description && (
              <p className={styles.linkedOffersSubtitle}>{offer.description}</p>
            )}

            <div className={styles.linkedOffersList}>
              {linkedOffers.map((productOffer, index) => {
                const product = productOffer.product;
                if (!product) return null;

                const cardId = `linked-${productOffer.id || index}`;
                const isFirst = index === 0;
                // Same resolution order the legacy super_offer_products card uses (board task 20)
                const offerImage = productOffer.heroImage
                  || product.lifestyleImageUrl
                  || product.images?.find(img => img.isPrimary)?.url
                  || product.images?.[0]?.url;

                return (
                  <div
                    key={productOffer.id || index}
                    className={`${styles.linkedOfferCard} ${isFirst ? styles.linkedOfferCardFeatured : ''} ${selectedProductId === cardId ? styles.linkedOfferCardSelected : ''}`}
                  >
                    {/* Category label - migrated from super_offer_products (board task 20) */}
                    {productOffer.categoryLabel && (
                      <p className={styles.categoryLabel}>
                        If you want <span className={styles.categoryBold}>{productOffer.categoryLabel}</span>
                      </p>
                    )}

                    {/* Badge */}
                    {productOffer.badgeText && (
                      <span className={styles.linkedOfferBadge}>{productOffer.badgeText}</span>
                    )}
                    {isFirst && !productOffer.badgeText && (
                      <span className={styles.linkedOfferBadge}>Best match</span>
                    )}

                    {/* Hero Image */}
                    {offerImage && (
                      <div className={styles.linkedOfferImage}>
                        <img loading="lazy" src={offerImage} alt={productOffer.title || product.name} />
                      </div>
                    )}

                    {/* Header */}
                    <div className={styles.linkedOfferHeader}>
                      <h2 className={styles.linkedOfferName}>
                        {productOffer.title || product.name}
                        <span className={styles.rxSymbol}>&#8478;</span>
                      </h2>
                      <p className={styles.linkedOfferSubtitle}>
                        {productOffer.description || product.shortDescription}
                      </p>
                    </div>

                    {/* FDA status - migrated from super_offer_products (board task 20).
                        Regulatory disclosure: must render on both offer paths. */}
                    {productOffer.fdaStatus && (
                      <p className={`${styles.fdaStatus} ${productOffer.fdaStatus.includes('not FDA') ? styles.compounded : ''}`}>
                        {productOffer.fdaStatus}
                      </p>
                    )}

                    {/* Features */}
                    {productOffer.features?.length > 0 && (
                      <ul className={styles.linkedOfferFeatures}>
                        {productOffer.features.map((feature, fIndex) => (
                          <li key={fIndex} className={styles.linkedOfferFeatureItem}>
                            <svg className={styles.checkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Price and CTA */}
                    <div className={styles.linkedOfferFooter}>
                      {productOffer.pricingDisplay && (
                        <span className={styles.linkedOfferPrice}>{productOffer.pricingDisplay}</span>
                      )}
                      <button
                        className={styles.linkedOfferCta}
                        onClick={() => handleLinkedOfferSelect(productOffer, cardId)}
                      >
                        {productOffer.ctaText || 'Get Started'}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </button>
                    </div>

                    <ConsentDisclaimer />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Hero Section from Super Offer Settings */}
            {(settings.heroTitle || settings.heroSubtitle || settings.heroImage || offer.bannerImage) && (
              <div className={styles.heroSection}>
                {(settings.heroImage || offer.bannerImage) && (
                  <div className={styles.heroImageWrap}>
                    <img src={settings.heroImage || offer.bannerImage} alt={settings.heroTitle || offer.name} className={styles.heroImg} />
                  </div>
                )}
                <div className={styles.heroContent}>
                  {settings.heroTitle && <h1 className={styles.heroTitle}>{settings.heroTitle}</h1>}
                  {settings.heroSubtitle && (
                    <div className={styles.heroSubtitle} dangerouslySetInnerHTML={{ __html: settings.heroSubtitle }} />
                  )}
                </div>
              </div>
            )}

            {/* Featured Product Card */}
            {featuredProduct && (
              <div className={styles.featuredCard}>
                <span className={styles.bestMatchBadge}>Best match</span>

                <div className={styles.featuredHeader}>
                  <h2 className={styles.featuredName}>
                    {featuredProduct.product?.name}
                    <span className={styles.rxSymbol}>&#8478;</span>
                  </h2>
                  <p className={styles.featuredSubtitle}>
                    {featuredProduct.customDescription || featuredProduct.product?.shortDescription}
                  </p>
                </div>

                {/* Product Image */}
                {(featuredProduct.product?.lifestyleImageUrl || featuredProduct.product?.images?.[0]) && (
                  <div className={styles.featuredImage}>
                    <img
                      src={featuredProduct.product.lifestyleImageUrl || featuredProduct.product.images.find(img => img.isPrimary)?.url || featuredProduct.product.images[0]?.url}
                      alt={featuredProduct.product.name}
                    />
                  </div>
                )}

                {/* Dosage Selection - only active dosages with prices. Doctor Now (provider metadata,
                    card 54405) shows the clinician notice instead; selectedDosage still prices it. */}
                {!canCustomerSelectDosage(featuredProduct.product) && (
                  <div className={styles.dosageSection}>
                    <span className={styles.dosageLabel}>Dosage</span>
                    <p className={styles.featuredSubtitle}>{DOCTOR_NOW_DOSAGE_NOTICE}</p>
                  </div>
                )}
                {canCustomerSelectDosage(featuredProduct.product) && (() => {
                  const activeDosages = getActiveDosages(featuredProduct.product?.dosages);
                  return activeDosages.length > 0 && (
                    <div className={styles.dosageSection}>
                      <label className={styles.dosageLabel}>Select dosage</label>
                      <select
                        className={styles.dosageSelect}
                        value={selectedDosage?.id || ''}
                        onChange={handleDosageChange}
                      >
                        {activeDosages.map(dosage => (
                          <option key={dosage.id} value={dosage.id}>
                            {formatDosageOption(dosage)}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {/* Stats */}
                {(stats.stat1Label || stats.stat2Label) && (
                  <div className={styles.statsRow}>
                    {stats.stat1Label && (
                      <div className={styles.stat}>
                        <span className={styles.statLabel}>{stats.stat1Label}</span>
                        <span className={styles.statValue}>{stats.stat1Value}</span>
                      </div>
                    )}
                    {stats.stat2Label && (
                      <div className={styles.stat}>
                        <span className={styles.statLabel}>{stats.stat2Label}</span>
                        <span className={styles.statValue}>{stats.stat2Value}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Features List */}
                {features.length > 0 && (
                  <ul className={styles.featuresList}>
                    {features.map((feature, index) => (
                      <li key={index} className={styles.featureItem}>
                        <svg className={styles.checkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span className={feature.isUnderlined ? styles.underlined : ''}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Price and CTA */}
                <div className={styles.featuredFooter}>
                  <div className={styles.priceAndSupply}>
                    {/* Shown against the promo-adjusted base month rate — the same number passed
                        to SupplyMonthSelector as `pricePerMonth`. Longer supply terms are cheaper
                        still, so this understates the saving rather than overstating it, which is
                        the safe direction for a price claim. */}
                    <RetailSavings
                      retailPrice={selectedDosage?.retailPrice}
                      listPrice={selectedDosage?.price}
                      displayedPrice={applyPromoDiscount(
                        selectedDosage?.price || featuredProduct.customPrice || featuredProduct.product?.pricing?.[0]?.price || 0,
                        featuredProduct.product?.systemPromo,
                        1
                      )}
                    />
                    <SupplyMonthSelector
                      pricePerMonth={applyPromoDiscount(
                        selectedDosage?.price || featuredProduct.customPrice || featuredProduct.product?.pricing?.[0]?.price || 0,
                        featuredProduct.product?.systemPromo,
                        1
                      )}
                      supplyPricing={getAdjustedSupplyPricing(
                        selectedDosage?.supplyPricing,
                        selectedDosage?.price || featuredProduct.customPrice || 0,
                        featuredProduct.product?.systemPromo
                      )}
                      selectedMonths={supplyMonths}
                      onChange={setSupplyMonths}
                    />
                  </div>
                  <button
                    className={styles.continueButton}
                    onClick={() => handleProductSelect(featuredProduct, 'featured')}
                  >
                    Continue
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </button>
                </div>

                <ConsentDisclaimer />
              </div>
            )}

            {/* Divider */}
            {alternativeProducts.length > 0 && (
              <>
                <div className={styles.divider} />

                {/* Alternative Options Section */}
                <div className={styles.alternativesSection}>
                  <h2 className={styles.alternativesTitle}>Explore other treatment options</h2>

                  <div className={styles.alternativesList}>
                    {alternativeProducts.map((offerProduct, index) => {
                      const product = offerProduct.product;
                      if (!product) return null;

                      const categoryLabel = offerProduct.categoryLabel || settings.categoryLabels?.[index] || '';
                      const fdaStatus = offerProduct.fdaStatus || '';
                      const priceDisplay = product.startingAtPrice
                        ? `from $${Math.ceil(product.startingAtPrice / 100)}`
                        : offerProduct.customPrice
                          ? `$${Math.ceil(offerProduct.customPrice / 100)}`
                          : product.pricing?.[0]?.price
                            ? `from $${Math.ceil(product.pricing[0].price / 100)}`
                            : '';
                      const priceUnit = offerProduct.priceUnit || '/dose';

                      const cardId = `alt-${offerProduct.id || index}`;
                      return (
                        <div
                          key={offerProduct.id || index}
                          className={`${styles.alternativeCard} ${selectedProductId === cardId ? styles.alternativeCardSelected : ''}`}
                        >
                          {categoryLabel && (
                            <p className={styles.categoryLabel}>
                              If you want <span className={styles.categoryBold}>{categoryLabel}</span>
                            </p>
                          )}

                          <div className={styles.altProductHeader}>
                            {(product.lifestyleImageUrl || product.images?.[0]) && (
                              <img
                                src={product.lifestyleImageUrl || product.images.find(img => img.isPrimary)?.url || product.images[0]?.url}
                                alt={product.name}
                                className={styles.altProductImage}
                              />
                            )}
                            <div className={styles.altProductInfo}>
                              <h3 className={styles.altProductName}>
                                {product.name}
                                <span className={styles.rxSymbol}>&#8478;</span>
                              </h3>
                              <p className={styles.altProductDosage}>
                                {offerProduct.customDescription || product.shortDescription}
                              </p>
                            </div>
                          </div>

                          <p className={styles.altProductDesc}>
                            {offerProduct.longDescription || product.description || ''}
                          </p>

                          {fdaStatus && (
                            <p className={`${styles.fdaStatus} ${fdaStatus.includes('not FDA') ? styles.compounded : ''}`}>
                              {fdaStatus}
                            </p>
                          )}

                          {/* Dosage Selector - only active dosages with prices. Doctor Now (provider
                              metadata, card 54405) shows the clinician notice instead. */}
                          {!canCustomerSelectDosage(product) && (
                            <div className={styles.altDosageSelector}>
                              <p className={styles.altProductDosage}>{DOCTOR_NOW_DOSAGE_NOTICE}</p>
                            </div>
                          )}
                          {canCustomerSelectDosage(product) && (() => {
                            const activeDosages = getActiveDosages(product.dosages);
                            const cardKey = offerProduct.id || index;
                            const defaultActiveDosage = activeDosages.find(d => d.isDefault) || activeDosages[0];
                            return activeDosages.length > 1 && (
                              <div className={styles.altDosageSelector}>
                                <label className={styles.altDosageLabel}>Dosage:</label>
                                <select
                                  className={styles.altDosageDropdown}
                                  value={otherDosages[cardKey]?.id || defaultActiveDosage?.id || ''}
                                  onChange={(e) => {
                                    const dosage = activeDosages.find(d => d.id === e.target.value);
                                    handleOtherDosageChange(cardKey, dosage);
                                  }}
                                >
                                  {activeDosages.map(d => (
                                    <option key={d.id} value={d.id}>
                                      {formatDosageOption(d)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            );
                          })()}

                          <div className={styles.altSupplySelector}>
                            {(() => {
                              const cardKey = offerProduct.id || index;
                              const activeDosages = getActiveDosages(product.dosages);
                              const defaultActiveDosage = activeDosages.find(d => d.isDefault) || activeDosages[0];
                              const shownDosage = otherDosages[cardKey] || defaultActiveDosage;
                              return (
                                <>
                                <RetailSavings
                                  retailPrice={shownDosage?.retailPrice}
                                  listPrice={shownDosage?.price}
                                  displayedPrice={applyPromoDiscount(
                                    shownDosage?.price || offerProduct.customPrice || product.pricing?.[0]?.price || 0,
                                    product.systemPromo,
                                    1
                                  )}
                                />
                                <SupplyMonthSelector
                                  pricePerMonth={applyPromoDiscount(
                                    otherDosages[cardKey]?.price ||
                                    offerProduct.customPrice ||
                                    defaultActiveDosage?.price ||
                                    product.pricing?.[0]?.price ||
                                    0,
                                    product.systemPromo,
                                    1
                                  )}
                                  supplyPricing={getAdjustedSupplyPricing(
                                    otherDosages[cardKey]?.supplyPricing || defaultActiveDosage?.supplyPricing,
                                    otherDosages[cardKey]?.price || defaultActiveDosage?.price || 0,
                                    product.systemPromo
                                  )}
                                  selectedMonths={otherSupplyMonths[cardKey] || 3}
                                  onChange={(months) => handleOtherSupplyMonthsChange(cardKey, months)}
                                />
                                </>
                              );
                            })()}
                          </div>

                          <div className={styles.altProductFooter}>
                            <button
                              className={styles.arrowButton}
                              onClick={() => handleProductSelect(offerProduct, cardId)}
                            >
                              Select
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                              </svg>
                            </button>
                          </div>

                          <ConsentDisclaimer />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Footnote */}
        {footnote && (
          <p className={styles.footnote}>{footnote}</p>
        )}
      </div>

      {/* Horizontal Product Scroller */}
      {showAllProducts && (
        <div className={styles.scrollerOverlay}>
          <div className={styles.scrollerPanel}>
            <div className={styles.scrollerHeader}>
              <h3 className={styles.scrollerTitle}>All treatment options</h3>
              <button
                className={styles.scrollerClose}
                onClick={handleToggleAllProducts}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className={styles.scrollerContainer}>
              {loadingProducts ? (
                <div className={styles.scrollerLoading}>Loading products...</div>
              ) : productsError ? (
                <div className={styles.scrollerEmpty}>
                  {productsError}
                  <button
                    type="button"
                    className={styles.scrollerRetry}
                    onClick={fetchCategoryProducts}
                  >
                    Try again
                  </button>
                </div>
              ) : categoryProducts.length === 0 ? (
                <div className={styles.scrollerEmpty}>No products available</div>
              ) : (
                categoryProducts.map((product) => {
                  const price = product.startingAtPrice || product.pricing?.[0]?.price || product.dosages?.[0]?.price;
                  const priceDisplay = price ? `$${Math.ceil(price / 100)}` : '';
                  const startingDosage = getStartingDosage(product.dosages);

                  return (
                    <div
                      key={product.id}
                      className={`${styles.scrollerCard} ${selectedProductId === product.id ? styles.scrollerCardSelected : ''}`}
                      onClick={() => handleProductSelect({ product }, product.id)}
                    >
                      <div className={styles.scrollerCardImage}>
                        {(product.lifestyleImageUrl || product.images?.[0]) ? (
                          <img
                            src={product.lifestyleImageUrl || product.images.find(img => img.isPrimary)?.url || product.images[0]?.url}
                            alt={product.name}
                          />
                        ) : (
                          <div className={styles.scrollerCardPlaceholder}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-6 18h6" />
                            </svg>
                            <span>{product.name}</span>
                          </div>
                        )}
                      </div>
                      <div className={styles.scrollerCardContent}>
                        <h4 className={styles.scrollerCardName}>
                          {product.name}
                          <span className={styles.rxSymbol}>&#8478;</span>
                        </h4>
                        <p className={styles.scrollerCardDesc}>
                          {product.shortDescription}
                        </p>
                        {priceDisplay && (
                          <span className={styles.scrollerCardPrice}>
                            {/* The card is 160px wide, so this line wraps. `.scrollerContainer` is
                                a flex row whose items stretch, so every card grows together and
                                nothing is clipped — see the scoped type sizes in the stylesheet. */}
                            <RetailSavings
                              retailPrice={startingDosage?.retailPrice}
                              listPrice={startingDosage?.price}
                              displayedPrice={price}
                            />
                            from {priceDisplay}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Skip Button - only for product-specific triggers */}
      {originalProductSlug && (
        <div className={styles.skipSection}>
          <button className={styles.skipButton} onClick={handleSkip}>
            Continue with my original selection
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      )}

      {/* Floating "View All Options" Button */}
      <button
        className={`${styles.floatingButton} ${showAllProducts ? styles.floatingButtonActive : ''}`}
        onClick={handleToggleAllProducts}
      >
        {showAllProducts ? 'Close' : 'View all options'}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {showAllProducts ? (
            <>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </>
          ) : (
            <>
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </>
          )}
        </svg>
      </button>

      {/* Loading overlay when checking intake status */}
      {checkingIntake && (
        <div className={styles.intakeCheckOverlay}>
          <div className={styles.intakeCheckSpinner} />
          <span>Checking your intake status...</span>
        </div>
      )}
    </div>
  );
};

export default OfferPage;
